/**
 * WebSocket Connection Manager - Singleton Pattern
 * 
 * Manages all WebSocket connections to prevent duplicate connections
 * and handle reconnection logic centrally.
 */

import { ManagedConnection } from './ManagedConnection';

export enum ConnectionType {
  CHAT = 'chat',
  DOCUMENT = 'document',
  NOTIFICATION = 'notification'
}

export enum ConnectionStatus {
  DISCONNECTED = 'disconnected',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  RECONNECTING = 'reconnecting',
  ERROR = 'error',
  AUTH_FAILED = 'auth_failed'
}

export interface SubscriptionConfig {
  onMessage: (message: any) => void;
  onStatusChange: (status: ConnectionStatus) => void;
}

export interface SubscriptionHandle {
  id: string;
  type: ConnectionType;
  unsubscribe: () => void;
}

class WebSocketConnectionManager {
  private static instance: WebSocketConnectionManager;
  // Global registry to track WebSocket connections across component remounts
  // This prevents duplicate connections when React StrictMode double-mounts components
  private static globalConnections: Map<string, ManagedConnection> = new Map();
  private static currentUserToken: string | null = null;
  
  private connections: Map<ConnectionType, ManagedConnection>;
  private subscribers: Map<ConnectionType, Map<string, SubscriptionConfig>>;
  private subscriberCount: Map<ConnectionType, number>;
  private getUrlFunctions: Map<ConnectionType, () => Promise<string>>;
  private urlCache: Map<ConnectionType, { url: string; timestamp: number }>;
  private urlRequestInProgress: Map<ConnectionType, Promise<string>>;
  private readonly URL_CACHE_TTL = 30000; // 30 seconds cache for URLs
  
  private constructor() {
    this.connections = new Map();
    this.subscribers = new Map();
    this.subscriberCount = new Map();
    this.getUrlFunctions = new Map();
    this.urlCache = new Map();
    this.urlRequestInProgress = new Map();
    
    console.log('[WSManager] Singleton instance created');
  }
  
  static getInstance(): WebSocketConnectionManager {
    if (!this.instance) {
      this.instance = new WebSocketConnectionManager();
    }
    return this.instance;
  }
  
  /**
   * Set the current user token (used for global connection tracking)
   */
  setUserToken(token: string | null): void {
    if (WebSocketConnectionManager.currentUserToken !== token) {
      console.log(`[WSManager] User token changed, clearing global connections`);
      // Clear all global connections when user changes
      WebSocketConnectionManager.globalConnections.forEach((connection, key) => {
        console.log(`[WSManager] Disconnecting global connection: ${key}`);
        connection.disconnect();
      });
      WebSocketConnectionManager.globalConnections.clear();
      WebSocketConnectionManager.currentUserToken = token;
    }
  }
  
  /**
   * Register a URL provider function for a specific connection type
   * Only registers if not already registered or if force is true
   */
  registerUrlProvider(type: ConnectionType, getUrl: () => Promise<string>, force: boolean = false): void {
    // Skip if already registered unless forced
    if (!force && this.getUrlFunctions.has(type)) {
      console.log(`[WSManager] URL provider already registered for ${type}, skipping`);
      return;
    }
    
    // Wrap the URL provider with caching and deduplication logic
    const cachedGetUrl = async (): Promise<string> => {
      // Check cache first
      const cached = this.urlCache.get(type);
      if (cached && (Date.now() - cached.timestamp < this.URL_CACHE_TTL)) {
        console.log(`[WSManager] Using cached URL for ${type}`);
        return cached.url;
      }
      
      // Check if a request is already in progress
      const inProgress = this.urlRequestInProgress.get(type);
      if (inProgress) {
        console.log(`[WSManager] URL request already in progress for ${type}, waiting...`);
        return inProgress;
      }
      
      // Make the request and cache it
      console.log(`[WSManager] Fetching new URL for ${type}`);
      const urlPromise = getUrl().then(url => {
        // Cache the result
        this.urlCache.set(type, { url, timestamp: Date.now() });
        // Clear the in-progress flag
        this.urlRequestInProgress.delete(type);
        return url;
      }).catch(error => {
        // On error, keep the in-progress flag for a short time to prevent rapid retries
        console.error(`[WSManager] URL fetch failed for ${type}, blocking retries for 5s:`, error);
        setTimeout(() => {
          this.urlRequestInProgress.delete(type);
        }, 5000); // Block retries for 5 seconds after an error
        throw error;
      });
      
      // Mark as in progress
      this.urlRequestInProgress.set(type, urlPromise);
      
      return urlPromise;
    };
    
    this.getUrlFunctions.set(type, cachedGetUrl);
    console.log(`[WSManager] Registered cached URL provider for ${type}`);
  }
  
  /**
   * Subscribe to a WebSocket connection
   * Creates connection if needed, increments reference count
   */
  subscribe(
    type: ConnectionType,
    config: SubscriptionConfig
  ): SubscriptionHandle {
    const id = this.generateUniqueId();
    
    console.log(`[WSManager] New subscription request: type=${type}, id=${id}`);
    
    // Initialize subscriber tracking for this type if needed
    if (!this.subscribers.has(type)) {
      this.subscribers.set(type, new Map());
      this.subscriberCount.set(type, 0);
    }
    
    // Add subscriber
    this.subscribers.get(type)!.set(id, config);
    const newCount = (this.subscriberCount.get(type) || 0) + 1;
    this.subscriberCount.set(type, newCount);
    
    console.log(`[WSManager] Subscriber count for ${type}: ${newCount}`);
    
    // Create or reuse connection
    if (!this.connections.has(type)) {
      this.createConnection(type);
    }
    
    // Connect if first subscriber
    if (newCount === 1) {
      console.log(`[WSManager] First subscriber for ${type}, connecting...`);
      this.connections.get(type)?.connect();
    } else {
      // Notify new subscriber of current status
      const connection = this.connections.get(type);
      if (connection) {
        config.onStatusChange(connection.getStatus());
      }
    }
    
    return {
      id,
      type,
      unsubscribe: () => this.unsubscribe(type, id)
    };
  }
  
  /**
   * Unsubscribe from a connection
   * Decrements reference count and schedules disconnection if no subscribers
   */
  private unsubscribe(type: ConnectionType, id: string): void {
    console.log(`[WSManager] Unsubscribe request: type=${type}, id=${id}`);
    
    const typeSubscribers = this.subscribers.get(type);
    if (!typeSubscribers) {
      console.warn(`[WSManager] No subscribers found for type ${type}`);
      return;
    }
    
    typeSubscribers.delete(id);
    const count = Math.max(0, (this.subscriberCount.get(type) || 1) - 1);
    this.subscriberCount.set(type, count);
    
    console.log(`[WSManager] Subscriber count for ${type} after unsubscribe: ${count}`);
    
    // Schedule disconnection if no more subscribers
    if (count === 0) {
      this.scheduleDisconnection(type);
    }
  }
  
  /**
   * Send a message through a specific connection type
   */
  send(type: ConnectionType, message: any): void {
    const connection = this.connections.get(type);
    if (connection) {
      connection.send(message);
    } else {
      console.warn(`[WSManager] No connection available for type ${type}`);
    }
  }
  
  /**
   * Get the current status of a connection
   */
  getConnectionStatus(type: ConnectionType): ConnectionStatus {
    const connection = this.connections.get(type);
    return connection ? connection.getStatus() : ConnectionStatus.DISCONNECTED;
  }
  
  /**
   * Force reconnect for a specific connection type
   */
  reconnect(type: ConnectionType): void {
    const connection = this.connections.get(type);
    if (connection) {
      console.log(`[WSManager] Manual reconnect requested for ${type}`);
      connection.reconnect();
    }
  }
  
  /**
   * Create a new managed connection
   */
  private createConnection(type: ConnectionType): void {
    // First check global registry to prevent duplicate connections
    const globalKey = this.getGlobalConnectionKey(type);
    const existingGlobal = WebSocketConnectionManager.globalConnections.get(globalKey);
    
    if (existingGlobal && existingGlobal.getStatus() !== ConnectionStatus.DISCONNECTED) {
      console.log(`[WSManager] Reusing existing global connection for ${type} (key: ${globalKey})`);
      // Reuse the existing connection
      this.connections.set(type, existingGlobal);
      
      // Note: The connection already exists, we just need to ensure our instance tracks it
      return;
    }
    
    const getUrl = this.getUrlFunctions.get(type);
    if (!getUrl) {
      console.error(`[WSManager] No URL provider registered for ${type}`);
      return;
    }
    
    console.log(`[WSManager] Creating new connection for ${type} (key: ${globalKey})`);
    
    const connection = new ManagedConnection(
      type,
      getUrl,
      (event: 'message' | 'status', data: any) => {
        this.notifySubscribers(type, event, data);
      }
    );
    
    // Store in both local and global registries
    this.connections.set(type, connection);
    WebSocketConnectionManager.globalConnections.set(globalKey, connection);
  }
  
  /**
   * Get the global connection key for a given connection type
   */
  private getGlobalConnectionKey(type: ConnectionType): string {
    const token = WebSocketConnectionManager.currentUserToken || 'anonymous';
    return `${token}_${type}`;
  }
  
  /**
   * Notify all subscribers of an event
   */
  private notifySubscribers(
    type: ConnectionType, 
    event: 'message' | 'status', 
    data: any
  ): void {
    const typeSubscribers = this.subscribers.get(type);
    if (!typeSubscribers) return;
    
    typeSubscribers.forEach(config => {
      try {
        if (event === 'message') {
          config.onMessage(data);
        } else if (event === 'status') {
          config.onStatusChange(data);
        }
      } catch (error) {
        console.error(`[WSManager] Error notifying subscriber:`, error);
      }
    });
  }
  
  /**
   * Schedule disconnection with grace period
   */
  private scheduleDisconnection(type: ConnectionType): void {
    console.log(`[WSManager] Scheduling disconnection for ${type} in 5 seconds`);
    
    // Grace period before closing connection (5 seconds)
    setTimeout(() => {
      if (this.subscriberCount.get(type) === 0) {
        console.log(`[WSManager] Disconnecting ${type} - no subscribers`);
        const connection = this.connections.get(type);
        if (connection) {
          connection.disconnect();
        }
        this.connections.delete(type);
      } else {
        console.log(`[WSManager] Disconnection cancelled for ${type} - new subscribers`);
      }
    }, 5000);
  }
  
  /**
   * Generate unique subscription ID
   */
  private generateUniqueId(): string {
    return `sub_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }
  
  /**
   * Cleanup all connections (for debugging/testing)
   */
  disconnectAll(): void {
    console.log('[WSManager] Disconnecting all connections');
    this.connections.forEach((connection) => {
      connection.disconnect();
    });
    this.connections.clear();
    this.subscribers.clear();
    this.subscriberCount.clear();
    
    // Also clear global connections
    WebSocketConnectionManager.globalConnections.forEach((connection) => {
      connection.disconnect();
    });
    WebSocketConnectionManager.globalConnections.clear();
  }
  
  /**
   * Get debug info about current connections
   */
  getDebugInfo(): {
    connections: string[];
    globalConnections: string[];
    subscriberCounts: Record<string, number>;
    statuses: Record<string, ConnectionStatus>;
    currentUserToken: string | null;
  } {
    const connections = Array.from(this.connections.keys());
    const globalConnections = Array.from(WebSocketConnectionManager.globalConnections.keys());
    const subscriberCounts: Record<string, number> = {};
    const statuses: Record<string, ConnectionStatus> = {};
    
    connections.forEach(type => {
      subscriberCounts[type] = this.subscriberCount.get(type) || 0;
      const connection = this.connections.get(type);
      statuses[type] = connection ? connection.getStatus() : ConnectionStatus.DISCONNECTED;
    });
    
    return {
      connections,
      globalConnections,
      subscriberCounts,
      statuses,
      currentUserToken: WebSocketConnectionManager.currentUserToken
    };
  }
}

// Export singleton instance
export const wsManager = WebSocketConnectionManager.getInstance();

// Export for debugging in browser console
if (typeof window !== 'undefined') {
  (window as any).__wsManager = wsManager;
}